import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Zap, Wallet, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import InvestTradingModal from "@/components/InvestTradingModal";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";

export default function Activate() {
  const { user } = useAuth();
  const { data: dash, reload } = useFetch("/user/dashboard");
  const [mode, setMode] = useState("self");
  const [target, setTarget] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingTarget, setPendingTarget] = useState("");
  const [pendingName, setPendingName] = useState("");
  const [successInfo, setSuccessInfo] = useState(null);
  const fund = dash?.balances?.deposit_balance || 0;

  const onTargetChange = (v) => {
    setTarget(v); setLookup(null);
    const t = v.trim();
    if (t.length >= 4) {
      api.get(`/user/member-lookup?member_id=${encodeURIComponent(t)}`)
        .then((r) => setLookup({ ok: true, ...r.data }))
        .catch(() => setLookup({ ok: false }));
    }
  };

  const doActivate = async (tgt) => {
    setBusy(true);
    try {
      await api.post("/user/activate", { target_member_id: tgt, amount: Number(amount) });
      setSuccessInfo({ amount: Number(amount), at: new Date().toLocaleString(), memberId: tgt });
      setShowGraph(false); setConfirm(null); setSuccess(true); reload();
    } catch (err) { setShowGraph(false); toast.error(err.response?.data?.detail || "Activation failed"); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!Number(amount)) return toast.error("Enter a valid package amount");
    if (Number(amount) > Number(fund)) return toast.error("Amount exceeds your fund balance");
    if (mode === "self") { setPendingTarget(user?.member_id); setPendingName(`${user?.member_id} (You)`); setShowGraph(true); return; }
    const tgt = target.trim();
    if (!tgt) return toast.error("Enter the Member ID to activate");
    // Confirm target identity before spending fund
    setBusy(true);
    try {
      const { data } = await api.get(`/user/member-lookup?member_id=${encodeURIComponent(tgt)}`);
      setConfirm(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Member ID not found"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Activate ID" subtitle="Use your fund balance to activate your own or another member's ID" />
      <div className="grid gap-6 lg:grid-cols-[0.9fr_2.1fr]">
        <div className="space-y-6">
          <GlassCard className="p-6">
            <div className="flex items-center gap-2 text-[var(--text-dim)]"><Wallet size={16} className="text-[var(--gold)]" /> Fund Balance</div>
            <p className="mt-2 gold-text font-display text-3xl font-semibold" data-testid="activate-fund-balance">${money(fund)}</p>
            <p className="mt-2 text-xs text-[var(--text-dim)]">This is the balance available to activate IDs. Top it up via a deposit or ask an admin to credit fund.</p>
          </GlassCard>

          <div className="relative min-h-[310px] overflow-hidden rounded-3xl border border-white/20 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.95),transparent_28%),radial-gradient(circle_at_82%_78%,rgba(16,185,129,0.24),transparent_34%),linear-gradient(145deg,#f7f8f5_0%,#e6eee8_38%,#10251c_70%,#07100c_100%)] p-6 shadow-[0_24px_60px_rgba(0,0,0,0.28)]">
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full border border-emerald-400/30 bg-emerald-300/10" />
            <div className="absolute -bottom-24 -left-16 h-48 w-48 rounded-full border border-amber-300/25 bg-amber-200/10" />
            <div className="relative z-10">
              <div className="inline-flex rounded-full border border-slate-900/10 bg-white/75 px-3 py-1 text-[10px] font-extrabold tracking-[0.18em] text-slate-800">ALEXA FINTECH</div>
              <h3 className="mt-5 max-w-[220px] font-display text-3xl font-black leading-[0.95] text-slate-900">SMART<br />WEALTH<br /><span className="text-emerald-700">ACTIVATION</span></h3>
              <p className="mt-4 max-w-[235px] text-xs font-semibold leading-5 text-slate-700/80">Secure package activation with a premium financial dashboard experience.</p>
            </div>
            <svg viewBox="0 0 360 150" className="absolute bottom-3 right-2 w-[72%] opacity-95" aria-hidden="true">
              <defs>
                <linearGradient id="activatePageLine" x1="0" x2="1">
                  <stop offset="0%" stopColor="#0f766e" stopOpacity="0.25" />
                  <stop offset="55%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#d4a72c" />
                </linearGradient>
              </defs>
              <path d="M8 126 L42 112 L70 118 L105 86 L136 98 L168 67 L198 78 L231 49 L260 60 L296 28 L348 40" fill="none" stroke="url(#activatePageLine)" strokeWidth="5" strokeLinecap="round" />
              <path d="M8 126 L42 112 L70 118 L105 86 L136 98 L168 67 L198 78 L231 49 L260 60 L296 28 L348 40 L348 150 L8 150 Z" fill="url(#activatePageLine)" opacity="0.12" />
              <circle cx="296" cy="28" r="8" fill="#d4a72c" />
            </svg>
            <div className="absolute bottom-5 left-6 right-6 flex gap-2">
              <div className="rounded-xl border border-white/60 bg-white/60 px-3 py-2 backdrop-blur">
                <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600">Secure</div>
                <div className="text-xs font-extrabold text-slate-900">Protected</div>
              </div>
              <div className="rounded-xl border border-white/30 bg-slate-900/35 px-3 py-2 backdrop-blur">
                <div className="text-[9px] font-bold uppercase tracking-wider text-white/65">Status</div>
                <div className="text-xs font-extrabold text-emerald-300">Ready</div>
              </div>
            </div>
          </div>
        </div>

        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Zap size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">Activate an ID</h3></div>
          <div className="mb-4 inline-flex rounded-xl glass-2 p-1">
            <button type="button" onClick={() => setMode("self")} className={cn("rounded-lg px-4 py-2 text-sm font-medium transition-colors", mode === "self" ? "btn3d-gold text-black" : "text-[var(--text-dim)]")} data-testid="activate-mode-self">My ID</button>
            <button type="button" onClick={() => setMode("other")} className={cn("rounded-lg px-4 py-2 text-sm font-medium transition-colors", mode === "other" ? "btn3d-gold text-black" : "text-[var(--text-dim)]")} data-testid="activate-mode-other">Another ID</button>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="activate-form">
            <div>
              <label className="mb-1 block text-xs text-[var(--text-dim)]">Member ID to activate</label>
              {mode === "self"
                ? <input className={inp + " text-[var(--text-dim)]"} value={user?.member_id || ""} readOnly data-testid="activate-self-id" />
                : <input className={inp} placeholder="Another member's ID (e.g. AF100025)" value={target} onChange={(e) => onTargetChange(e.target.value)} data-testid="activate-target" />}
              {mode === "other" && lookup && (
                lookup.ok
                  ? <p className="mt-1.5 text-xs font-semibold text-emerald-400" data-testid="activate-lookup-name">✓ {lookup.name || "Member"} · <span className="capitalize text-[var(--text-dim)]">{lookup.status}</span></p>
                  : <p className="mt-1.5 text-xs font-semibold text-rose-400" data-testid="activate-lookup-none">Member ID not found</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs text-[var(--text-dim)]">Package amount</label>
              <input type="number" min="0" step="any" className={inp} placeholder="Package amount (qualifying business)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="activate-amount" />
            </div>
            <p className="rounded-lg glass-2 p-2 text-[11px] text-[var(--text-dim)]">The amount is deducted from your fund balance and starts a fresh earning cycle on the activated ID.</p>
            <LuxuryButton type="submit" variant="gold" disabled={busy} data-testid="activate-submit">
              <Zap size={15} className="mr-1 inline" /> {busy ? "Please wait…" : (mode === "self" ? "Activate" : "Continue")}
            </LuxuryButton>
          </form>
        </GlassCard>
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="border-white/10 bg-[var(--bg-2)] text-white" data-testid="activate-confirm-modal">
          <DialogHeader><DialogTitle>Confirm Activation</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--text-dim)]">You are about to activate another member's ID using your fund balance. Please confirm the details:</p>
              <div className="space-y-2 rounded-xl glass-2 p-3 text-sm">
                <div className="flex justify-between"><span className="text-[var(--text-dim)]">Member ID</span><span className="font-semibold" data-testid="confirm-member-id">{confirm.member_id}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-dim)]">Name</span><span className="font-semibold" data-testid="confirm-member-name">{confirm.name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[var(--text-dim)]">Current status</span><span className="capitalize">{confirm.status}</span></div>
                <div className="flex justify-between border-t border-white/10 pt-2"><span className="text-[var(--text-dim)]">Amount from your fund</span><span className="gold-text font-bold">${money(amount)}</span></div>
              </div>
              <div className="flex gap-2">
                <LuxuryButton variant="dark" className="flex-1" onClick={() => setConfirm(null)} data-testid="confirm-cancel">Cancel</LuxuryButton>
                <LuxuryButton variant="gold" className="flex-1" onClick={() => { setPendingTarget(confirm.member_id); setPendingName(confirm.name ? `${confirm.name} · ${confirm.member_id}` : confirm.member_id); setConfirm(null); setShowGraph(true); }} data-testid="confirm-activate">
                  Continue to Invest
                </LuxuryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InvestTradingModal open={showGraph} amount={amount} busy={busy} targetName={pendingName} memberId={pendingTarget}
        onCancel={() => setShowGraph(false)}
        onConfirm={() => doActivate(pendingTarget)} />

      <Dialog open={success} onOpenChange={(o) => { if (!o) { setSuccess(false); setAmount(""); setTarget(""); setLookup(null); } }}>
        <DialogContent className="border-emerald-500/30 bg-[var(--bg-2)] text-center text-white" data-testid="invest-success-modal">
          <DialogHeader><DialogTitle className="text-center">Activation Successful</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="grid h-16 w-16 place-items-center rounded-full" style={{ background: "linear-gradient(160deg,#34d399,#059669)" }}><CheckCircle2 size={30} className="text-white" /></div>
            <p className="text-lg font-bold" style={{ color: "#34d399" }}>Your account activation was completed successfully.</p>
            {successInfo && (
              <div className="w-full rounded-xl p-4 text-left text-sm" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {[["Member ID", successInfo.memberId], ["Activated Amount", `$${money(successInfo.amount)}`], ["Status", "Active"], ["Activated At", successInfo.at]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-1.5 last:border-0"><span className="text-white/60">{k}</span><span className="font-semibold text-white">{v}</span></div>
                ))}
              </div>
            )}
            <p className="text-xs text-white/60">Your investment is now working with ALEXA Bot. Start self-trading to grow your own profit.</p>
            <LuxuryButton variant="gold" className="mt-1 w-full" onClick={() => window.location.assign("/app/trading")} data-testid="invest-success-close">GO TO ALEXA TRADING BOT</LuxuryButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
